import { Call, Device } from '@twilio/voice-sdk';
// eslint-disable-next-line camelcase
import { post_api } from '../../../redux/api_funcs';
// eslint-disable-next-line camelcase
import { get_api_url } from '../../../utils/urls';
import { getOpenTabsCount, getTabId } from './device_diagnostics';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

const DIAG_ENDPOINT = '/v2/twilio_call/client_diag';

// The backend rejects a diag row without a call_uuid, so every event is capped
// per call rather than globally.
const MAX_EVENTS_PER_CALL = 30;
const WARNING_DEDUPE_MS = 10000;
// Only ever one Twilio call is live in a CMS tab (the incoming manager blocks
// outbound and vice versa), so a single pending buffer is unambiguous.
const MAX_BUFFERED_EVENTS = 30;
// Bytes have to be flowing by the time a real leg has been up this long.
const DEAD_MEDIA_GRACE_MS = 6000;
// Three consecutive silent samples (the SDK emits one per second) before we
// call the mic dead — a single zero happens on normal speech pauses.
const ZERO_INPUT_SAMPLES = 3;
const MIC_CHECK_DELAY_MS = 1500;

// Warning names are built by the SDK as <prefix><metric> (see WARNING_PREFIXES /
// WARNING_NAMES in @twilio/voice-sdk call.js) plus the standalone
// 'ice-connectivity-lost'. constant-audio-output-level is listed for
// completeness but the SDK suppresses its emit.
const MEDIA_WARNINGS = new Set([
  'ice-connectivity-lost',
  'low-bytes-received',
  'low-bytes-sent',
  'low-mos',
  'constant-audio-input-level',
  'constant-audio-output-level'
]);

// The subset that means the other side is likely hearing nothing.
const USER_FACING_WARNINGS = new Set([
  'ice-connectivity-lost',
  'low-bytes-received',
  'low-bytes-sent'
]);

const AUDIO_WARNING_MESSAGE =
  'Audio problem on this call — the other side may not hear you. Check your headset and network.';
const DEAD_MEDIA_MESSAGE =
  'No audio is flowing on this call — the other side cannot hear you. Check your headset and network.';
const MIC_FAILURE_MESSAGE =
  'No microphone audio is being sent — check your mic/headset and browser mic permission.';

// call_uuid -> events already posted for that call.
const sentCounts = new Map();
// `${call_uuid}|${warning name}` -> last post time.
const warningSeenAt = new Map();
// Events raised before the call_uuid was known (outbound: it arrives on the
// `outbound_call_started` relay, after the SDK call exists).
let pendingEvents = [];

const resolveUuid = (callUuid) => {
  try {
    return (typeof callUuid === 'function' ? callUuid() : callUuid) || null;
  } catch (e) {
    return null;
  }
};

const prune = (map) => {
  if (map.size <= 50) return;
  const oldest = map.keys().next().value;
  map.delete(oldest);
};

const postDiag = (payload) => {
  const count = sentCounts.get(payload.call_uuid) || 0;
  if (count >= MAX_EVENTS_PER_CALL) return;
  sentCounts.set(payload.call_uuid, count + 1);
  prune(sentCounts);
  try {
    const sent = post_api(getUrl(DIAG_ENDPOINT), payload, true);
    if (sent && typeof sent.catch === 'function') {
      sent.catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('twilio_call.diag_post_failed', payload.event, err?.message || err);
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('twilio_call.diag_post_threw', payload.event, err?.message || err);
  }
};

/**
 * Snapshot of everything about this browser leg that a recording cannot show.
 * Every field is best-effort: a browser that lacks an API contributes null
 * rather than failing the snapshot.
 */
export const buildClientSnapshot = async (device, call) => {
  const snapshot = {
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    sdk_version: null,
    edge: null,
    // Device.region has no public getter in 2.18.x; it stays null unless a
    // later SDK exposes one.
    region: null,
    codec: null,
    input_device_label: null,
    output_device_labels: [],
    mic_permission: 'unavailable',
    audio_context_state: null,
    // Extra keys the diag backend does not model; they are carried in the
    // 'accept' event `data` as well so they survive into events JSON.
    tab_id: null,
    open_tabs: null
  };

  try {
    snapshot.tab_id = getTabId();
    snapshot.open_tabs = getOpenTabsCount();
  } catch (e) {
    snapshot.tab_id = null;
  }

  try {
    snapshot.sdk_version = Device.version || null;
  } catch (e) {
    snapshot.sdk_version = null;
  }
  try {
    snapshot.edge = device?.edge ?? null;
    snapshot.region = device?.region ?? null;
  } catch (e) {
    snapshot.edge = null;
  }
  try {
    snapshot.audio_context_state = Device.audioContext?.state ?? null;
  } catch (e) {
    snapshot.audio_context_state = null;
  }
  try {
    snapshot.codec = call?.codec || null;
  } catch (e) {
    snapshot.codec = null;
  }
  try {
    snapshot.input_device_label = call?.getLocalStream?.()?.getAudioTracks?.()[0]?.label || null;
  } catch (e) {
    snapshot.input_device_label = null;
  }
  try {
    const speakers = device?.audio?.speakerDevices?.get?.();
    if (speakers) {
      snapshot.output_device_labels = Array.from(speakers)
        .map((d) => d?.label)
        .filter(Boolean);
    }
  } catch (e) {
    snapshot.output_device_labels = [];
  }
  try {
    const status = await navigator.permissions.query({ name: 'microphone' });
    snapshot.mic_permission = status?.state || 'unavailable';
  } catch (e) {
    snapshot.mic_permission = 'unavailable';
  }

  return snapshot;
};

/**
 * Fire-and-forget diagnostic post. Never throws and never rejects.
 * `callUuid` may be a string or a getter, for call legs whose uuid only
 * resolves after the SDK call exists; a null uuid buffers until
 * flushClientDiag() supplies one.
 */
export const reportCallDiag = (callUuid, event, { callSid, snapshot, data } = {}) => {
  const uuid = resolveUuid(callUuid);
  const name = data?.name || null;

  if (name && (event === 'warning' || event === 'warning-cleared')) {
    const key = `${uuid || 'pending'}|${event}|${name}`;
    const last = warningSeenAt.get(key) || 0;
    const now = Date.now();
    if (now - last < WARNING_DEDUPE_MS) return;
    warningSeenAt.set(key, now);
    prune(warningSeenAt);
  }

  const payload = { call_uuid: uuid, event, at: new Date().toISOString() };
  if (callSid) payload.call_sid = callSid;
  if (snapshot) payload.snapshot = snapshot;
  if (data) payload.data = data;

  if (!uuid) {
    if (pendingEvents.length < MAX_BUFFERED_EVENTS) pendingEvents.push(payload);
    return;
  }
  postDiag(payload);
};

/** Post everything buffered while the call_uuid was unknown. */
export const flushClientDiag = (callUuid) => {
  const uuid = resolveUuid(callUuid);
  if (!uuid || pendingEvents.length === 0) return;
  const buffered = pendingEvents;
  pendingEvents = [];
  buffered.forEach((payload) => postDiag({ ...payload, call_uuid: uuid }));
};

/** Drop buffered events for a call that ended without ever resolving a uuid. */
export const discardPendingDiag = () => {
  pendingEvents = [];
};

/**
 * Wire media telemetry onto a live call. `onUserWarning(message, name)` is
 * called with a plain-English message when the agent needs to act, and with
 * `null` when the condition clears. Returns a detach function.
 */
export const attachMediaDiagnostics = (
  call,
  { callUuid, device, onStatus, onUserWarning } = {}
) => {
  if (!call || typeof call.on !== 'function') return () => {};

  const callSid = call?.parameters?.CallSid || null;
  const connectedAt = Date.now();
  let lastSample = null;
  let baselineBytesReceived = null;
  let zeroInputStreak = 0;
  let deadMediaReported = false;
  let warningCount = 0;
  let detached = false;

  const warn = (message, name) => {
    if (detached) return;
    try {
      onUserWarning?.(message, name);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('twilio_call.diag_user_warning_failed', e);
    }
  };

  const onWarning = (name, data) => {
    warningCount += 1;
    reportCallDiag(callUuid, 'warning', {
      callSid,
      data: { name, media: MEDIA_WARNINGS.has(name), detail: data || null }
    });
    if (USER_FACING_WARNINGS.has(name)) warn(AUDIO_WARNING_MESSAGE, name);
  };

  const onWarningCleared = (name) => {
    reportCallDiag(callUuid, 'warning-cleared', { callSid, data: { name } });
    if (USER_FACING_WARNINGS.has(name)) warn(null, name);
  };

  const onReconnecting = (err) => {
    reportCallDiag(callUuid, 'reconnecting', {
      callSid,
      data: { code: err?.code ?? null, message: err?.message || null }
    });
    try {
      onStatus?.('reconnecting');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('twilio_call.diag_status_failed', e);
    }
  };

  const onReconnected = () => {
    reportCallDiag(callUuid, 'reconnected', { callSid });
    warn(null, 'reconnecting');
    try {
      onStatus?.('connected');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('twilio_call.diag_status_failed', e);
    }
  };

  // A bridged-but-silent leg looks perfectly healthy to the SDK: no warning
  // fires because the thresholds need a baseline the leg never establishes.
  // Inbound totals that never move, or an input level pinned at zero, are the
  // only signals that separate it from a working call.
  const onSample = (sample) => {
    lastSample = sample;
    const received = sample?.totals?.bytesReceived ?? 0;
    if (baselineBytesReceived === null) baselineBytesReceived = received;
    let muted = false;
    try {
      muted = typeof call.isMuted === 'function' && call.isMuted();
    } catch (e) {
      muted = false;
    }
    zeroInputStreak = !muted && sample?.audioInputLevel === 0 ? zeroInputStreak + 1 : 0;

    if (deadMediaReported || Date.now() - connectedAt < DEAD_MEDIA_GRACE_MS) return;
    const noInbound = received <= baselineBytesReceived;
    const noInput = zeroInputStreak >= ZERO_INPUT_SAMPLES;
    if (!noInbound && !noInput) return;

    deadMediaReported = true;
    // eslint-disable-next-line no-console
    console.warn('twilio_call.dead_media_detected', { noInbound, noInput });
    reportCallDiag(callUuid, 'warning', {
      callSid,
      data: {
        name: 'dead-media-detected',
        reason: noInbound ? 'no-inbound-bytes' : 'zero-audio-input',
        sample
      }
    });
    warn(DEAD_MEDIA_MESSAGE, 'dead-media-detected');
  };

  const onError = (err) => {
    reportCallDiag(callUuid, 'error', {
      callSid,
      data: { code: err?.code ?? null, name: err?.name || null, message: err?.message || null }
    });
  };

  const onDisconnect = () => {
    reportCallDiag(callUuid, 'disconnect', {
      callSid,
      data: {
        stats: lastSample || null,
        edge: device?.edge ?? null,
        warning_count: warningCount,
        dead_media: deadMediaReported,
        duration_ms: Date.now() - connectedAt
      }
    });
    warn(null, 'disconnect');
  };

  call.on('warning', onWarning);
  call.on('warning-cleared', onWarningCleared);
  call.on('reconnecting', onReconnecting);
  call.on('reconnected', onReconnected);
  call.on('sample', onSample);
  call.on('error', onError);
  call.on('disconnect', onDisconnect);

  // Listener removal is deferred a tick: callers detach from their own
  // 'disconnect' handler, which the SDK registered before ours, so removing
  // synchronously would drop the final 'disconnect' diag event.
  const removeListeners = () => {
    if (typeof call.removeListener !== 'function') return;
    call.removeListener('warning', onWarning);
    call.removeListener('warning-cleared', onWarningCleared);
    call.removeListener('reconnecting', onReconnecting);
    call.removeListener('reconnected', onReconnected);
    call.removeListener('sample', onSample);
    call.removeListener('error', onError);
    call.removeListener('disconnect', onDisconnect);
  };

  return () => {
    if (detached) return;
    detached = true;
    setTimeout(removeListeners, 0);
  };
};

/**
 * Confirm the browser is actually sending mic audio once the leg has settled.
 * Returns a cancel function.
 */
export const verifyMicAfterAccept = (call, callUuid, onUserWarning) => {
  const timer = setTimeout(() => {
    let track = null;
    try {
      track = call?.getLocalStream?.()?.getAudioTracks?.()[0] || null;
    } catch (e) {
      track = null;
    }
    const micOk = !!track && track.readyState === 'live' && !track.muted && track.enabled;

    let sdkVersion = null;
    try {
      sdkVersion = Device.version || null;
    } catch (e) {
      sdkVersion = null;
    }

    reportCallDiag(callUuid, 'mic-check', {
      callSid: call?.parameters?.CallSid || null,
      snapshot: {
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        sdk_version: sdkVersion,
        input_device_label: track?.label || null,
        audio_context_state: Device.audioContext?.state ?? null,
        mic_ok: micOk
      },
      data: {
        ready_state: track?.readyState || null,
        muted: track ? !!track.muted : null,
        enabled: track ? !!track.enabled : null
      }
    });

    if (!micOk) {
      // eslint-disable-next-line no-console
      console.warn('twilio_call.mic_check_failed', { readyState: track?.readyState || null });
      try {
        onUserWarning?.(MIC_FAILURE_MESSAGE, 'mic-check');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('twilio_call.diag_user_warning_failed', e);
      }
    }
  }, MIC_CHECK_DELAY_MS);

  return () => clearTimeout(timer);
};

// Shared by both Device constructor sites. enableImprovedSignalingErrorPrecision
// stays off: it splits 53000 into 31xxx codes SELF_HEAL_ERROR_CODES does not cover.
export const DEVICE_OPTIONS = {
  codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
  logLevel: 'warn'
};
